import { inject, Injectable, InjectionToken } from '@angular/core'

import { MsalBroadcastService } from '@azure/msal-angular'
import { AuthError, EventMessage, EventType, InteractionType } from '@azure/msal-browser'
import * as Sentry from '@sentry/angular'

const UNKNOWN_ERROR_CODE = 'unknown'

/**
 * Sentry issue title. Keep it stable: Sentry groups on it, and the grouped issue's "users affected"
 * count is the number we are actually after.
 */
export const SILENT_FAILURE_MESSAGE = 'MSAL silent token renewal failed'

export type CaptureWarning = (message: string, context: { level: 'warning', tags: Record<string, string> }) => void

/**
 * The Sentry sink, behind a token so it can be substituted in tests.
 *
 * `@sentry/angular` is an ES module namespace and therefore frozen, so `spyOn(Sentry, ...)` throws
 * "not declared writable or has no setter". Injecting the call keeps the service testable without
 * reaching into the module object.
 */
export const CAPTURE_WARNING = new InjectionToken<CaptureWarning>('CAPTURE_WARNING', {
  providedIn: 'root',
  factory: () => (message, context) => { Sentry.captureMessage(message, context) },
})

/**
 * Build the Sentry tags for a silent token renewal failure, or `null` for any other event.
 *
 * We report *every* silent-flow failure rather than only `InteractionRequiredAuthError`, and tag the error
 * code so Sentry can separate causes we can fix from causes we cannot:
 *
 * - `timed_out` (subError `redirect_bridge_timeout`) means the redirect bridge did not answer within
 *   `system.iframeBridgeTimeout`. Since actedparis/acted-core#252 the bridge is adopted and this should not
 *   occur — if it reappears, a msal upgrade has probably moved one of the internals the bridge relies on.
 * - `login_required` / `interaction_required` are not bugs: Safari blocks the third-party Entra session
 *   cookie whatever we do, and conditional-access sign-in-frequency policies force re-auth by design.
 *
 * Either way the user is sent through an interactive redirect that reboots the app, so the rate is worth
 * knowing while nothing guards unsaved forms.
 */
export function silentFailureTags (message: EventMessage): Record<string, string> | null {
  if (message.eventType !== EventType.ACQUIRE_TOKEN_FAILURE) return null
  // a failed interactive redirect is a different problem, and would double-count the same session
  if (message.interactionType !== InteractionType.Silent) return null

  const errorCode = message.error instanceof AuthError ? message.error.errorCode : ''

  return {
    msal_error_code: errorCode || UNKNOWN_ERROR_CODE,
    // lets an Entra admin find this exact attempt in the tenant sign-in logs
    msal_correlation_id: message.correlationId,
  }
}

/**
 * Report silent token renewal failures to Sentry.
 *
 * Once the Entra refresh token expires (24h for a SPA), a failed silent renewal sends the user through an
 * interactive redirect, which reboots the app and discards any unsaved form. This service measures how often
 * that happens and in which browsers, so actedparis/acted-core#252 can be decided on data rather than on
 * support tickets — which arrive as "it logged me out", indistinguishable from an ordinary logout.
 *
 * Sentry already tags browser, os, release and environment, so nothing else needs sending.
 */
@Injectable({ providedIn: 'root' })
export class MsalTelemetryService {
  private readonly broadcastService = inject(MsalBroadcastService)
  private readonly captureWarning = inject(CAPTURE_WARNING)

  // the interceptor redirects away after the first failure, but a retry loop must not flood the project
  private reported = false

  constructor () {
    this.broadcastService.msalSubject$.subscribe((message: EventMessage) => {
      if (this.reported) return

      const tags = silentFailureTags(message)
      if (!tags) return

      this.reported = true
      // a warning, not an error: this degrades to an interactive login rather than breaking the app,
      // so it must not consume the error budget or trigger alerting
      this.captureWarning(SILENT_FAILURE_MESSAGE, { level: 'warning', tags })
    })
  }
}
